import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Plus, LogIn, PanelLeftClose, PanelLeft, User as UserIcon, Bot, Settings, Ticket, Shield, BookOpen, Cpu, Info, Compass, Trophy } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { useChatHistory } from '../hooks/useChatHistory';
import { useCredits } from '../hooks/useCredits';
import { ChatHistoryItem } from '../components/ChatHistoryItem';
import { ChatHistorySkeleton, CreditsBadgeSkeleton, CreditsDisplaySkeleton } from '@/components/skeletons';
import { cn } from '@/lib/utils';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Progress } from '@/components/ui/progress';

interface ChatSidebarProps {
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  user: User | null;
}

export const ChatSidebar = ({ onClose, collapsed = false, onToggleCollapse, user }: ChatSidebarProps) => {
  const { chats, loading, deleteChat } = useChatHistory(user?.id);
  const { creditsRemaining, creditsUsed, isGuest, loading: creditsLoading } = useCredits(user?.id);
  const { isAdmin } = useIsAdmin();
  const { getTextValue } = useFeatureFlags();
  const location = useLocation();

  const navItems = [
    { key: 'connect', icon: Bot, label: 'Connect your agent', path: '/connect', requiresAuth: false },
    { key: 'explore', icon: Compass, label: 'Explore debates', path: '/explore', requiresAuth: false },
    { key: 'models', icon: Cpu, label: 'Models', path: '/models', requiresAuth: false },
    { key: 'learn', icon: BookOpen, label: 'Learn', path: '/learn', requiresAuth: false },
    { key: 'scoreboard', icon: Trophy, label: 'Scoreboard', path: '/scoreboard', requiresAuth: false },
    { key: 'about', icon: Info, label: 'About', path: '/about', requiresAuth: false },
    { key: 'profile', icon: UserIcon, label: 'Profile', path: '/profile', requiresAuth: true },
    { key: 'agent-profiles', icon: Bot, label: 'Roles', path: '/agent-profiles', requiresAuth: true },
    { key: 'settings', icon: Settings, label: 'Settings', path: '/settings', requiresAuth: true },
    { key: 'admin', icon: Shield, label: 'Admin', path: '/admin', requiresAuth: true, adminOnly: true },
  ];

  // Get custom order from feature flags
  const customOrder = getTextValue('sidebar_nav_order');
  const orderedNavItems = customOrder
    ? customOrder.split(',').map(key => navItems.find(item => item.key === key.trim())).filter(Boolean)
    : navItems;
  
  // Add any missing items at the end
  const missingItems = navItems.filter(item => !orderedNavItems.some(o => o?.key === item.key));
  const finalNavItems = [...orderedNavItems, ...missingItems] as typeof navItems;

  // Filter nav items based on authentication and admin status
  const filteredNavItems = finalNavItems.filter(item => {
    if (item.requiresAuth && !user) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  // Group chats by date
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayChats = chats.filter(chat => {
    const chatDate = new Date(chat.created_at);
    return chatDate.toDateString() === today.toDateString();
  });

  const yesterdayChats = chats.filter(chat => {
    const chatDate = new Date(chat.created_at);
    return chatDate.toDateString() === yesterday.toDateString();
  });

  const olderChats = chats.filter(chat => {
    const chatDate = new Date(chat.created_at);
    return chatDate < yesterday;
  });

  if (collapsed) {
    return (
      <div className="flex flex-col h-full bg-card border-r items-center py-4 gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          title="Expand sidebar"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
        
        <div className="h-px w-8 bg-border my-2" />
        
        <Link to="/new" onClick={onClose}>
          <Button variant="default" size="icon" title="New debate">
            <Plus className="h-4 w-4" />
          </Button>
        </Link>
        
        <div className="flex-1" />
        
        {/* Credits Display */}
        <div className="flex flex-col items-center gap-1 mb-2" title={creditsLoading ? 'Loading credits...' : `${creditsRemaining} credits remaining`}>
          <Ticket className="h-4 w-4 text-muted-foreground" />
          {creditsLoading ? (
            <CreditsBadgeSkeleton />
          ) : (
            <Badge variant={creditsRemaining > 3 ? "secondary" : "destructive"} className="text-xs px-1.5 py-0">
              {creditsRemaining}
            </Badge>
          )}
        </div>
        
        <div className="h-px w-8 bg-border mb-2" />
        
        <div className="space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} onClick={onClose}>
                <Button variant="ghost" size="icon" title={item.label}>
                  <Icon className="h-4 w-4" />
                </Button>
              </Link>
            );
          })}
        </div>
        
        {!user && (
          <Link to="/auth" className="mt-2">
            <Button variant="outline" size="icon" title="Sign In">
              <LogIn className="h-4 w-4" />
            </Button>
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card border-r">
      {/* Branding */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <Link to="/new" className="flex items-center gap-2" onClick={onClose}>
            <span className="font-display text-2xl font-semibold">
              SiliconSoap
            </span>
          </Link>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="shrink-0"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Chat History */}
      <ScrollArea className="flex-1 p-2">
        <Link to="/new" className="block mb-5">
          <Button variant="default" className="w-full justify-start gap-2 bg-foreground text-background hover:bg-foreground/90" onClick={onClose}>
            <Plus className="h-4 w-4" />
            <span className="text-sm">New debate</span>
          </Button>
        </Link>
        <h2 className="mb-3 px-2 text-xs font-semibold uppercase text-muted-foreground">Your debates</h2>
        {user ? (
          <>
            {loading ? (
              <ChatHistorySkeleton />
            ) : chats.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                No saved chats yet
              </div>
            ) : (
              <div className="space-y-4">
                {todayChats.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground px-2 mb-2">TODAY</h3>
                    <div className="space-y-1">
                      {todayChats.map(chat => (
                        <ChatHistoryItem key={chat.id} chat={chat} onDelete={deleteChat} />
                      ))}
                    </div>
                  </div>
                )}

                {yesterdayChats.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground px-2 mb-2">YESTERDAY</h3>
                    <div className="space-y-1">
                      {yesterdayChats.map(chat => (
                        <ChatHistoryItem key={chat.id} chat={chat} onDelete={deleteChat} />
                      ))}
                    </div>
                  </div>
                )}

                {olderChats.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground px-2 mb-2">OLDER</h3>
                    <div className="space-y-1">
                      {olderChats.map(chat => (
                        <ChatHistoryItem key={chat.id} chat={chat} onDelete={deleteChat} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <Link to="/auth" className="block rounded-md border border-dashed p-4 text-sm text-muted-foreground hover:border-primary hover:text-foreground">Sign in to keep your debates and make them private</Link>
        )}
      </ScrollArea>

      {/* Footer Navigation */}
      <div className="border-t">
        {/* Credits Display */}
        <div className="p-4 border-b">
          {creditsLoading ? (
            <CreditsDisplaySkeleton />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Ticket className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">Free debates · {creditsRemaining} left</span>
                </div>
                <Badge
                  variant={creditsRemaining > 3 ? "secondary" : creditsRemaining > 0 ? "outline" : "destructive"}
                  className="sr-only font-semibold"
                >
                  {creditsRemaining}
                </Badge>
              </div>
              <Progress value={(() => { const total = creditsRemaining + creditsUsed; return Math.min(100, Math.max(0, total > 0 ? (creditsRemaining / total) * 100 : 0)); })()} className="h-1.5" />
              {isGuest && creditsRemaining > 0 && (
                <p className="text-xs text-muted-foreground">
                  Sign up for more
                </p>
              )}
              {creditsRemaining === 0 && (
                <Link to="/settings">
                  <Button variant="outline" size="sm" className="w-full text-xs">
                    Get More Credits
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>

        <Separator />
        <div className="p-2 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} onClick={onClose}>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full min-h-11 justify-start gap-2",
                    isActive && "bg-muted text-primary font-medium"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        {!user && (
          <div className="p-2 pt-0">
            <Separator className="mb-2" />
            <Link to="/auth">
              <Button variant="outline" className="w-full min-h-11 justify-start gap-2">
                <LogIn className="h-4 w-4" />
                Sign In
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
